import firebaseApp from "../firebase/credenciales";
import {
  collection,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  deleteField,
  getFirestore,
} from "firebase/firestore";
import {
  leerProcedencia,
  operacionAsignacionSede,
  quitarSedeDeUsuario,
} from "./coordenadasSedeService";

const db = getFirestore(firebaseApp);

const unique = (arr) =>
  Array.from(new Set((arr || []).filter(Boolean).map((v) => String(v))));

// Ejecuta operaciones async en lotes pequeños para no saturar Firestore
const ejecutarEnLotes = async (items, fn, tamano = 20) => {
  for (let i = 0; i < items.length; i += tamano) {
    await Promise.all(items.slice(i, i + tamano).map(fn));
  }
};

export const limpiarVinculosGrupoEnSedes = async ({ rucEmpresa, groupId, sedeIds }) => {
  const ids = unique(sedeIds);
  if (!rucEmpresa || !groupId || ids.length === 0) return;

  const batch = writeBatch(db);
  ids.forEach((sedeId) => {
    const sedeRef = doc(db, `company_cordenadas/${rucEmpresa}/sedes`, sedeId);
    batch.update(sedeRef, {
      groupId: deleteField(),
      groupName: deleteField(),
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
};

export const sincronizarMembresiasYCoordenadas = async ({
  rucEmpresa,
  groupId,
  groupName,
  currentMemberDnis,
  currentSedeIds,
  previousMemberDnis,
  previousSedeIds,
  sedesData = [],
  companyName = "Mi Empresa",
}) => {
  if (!rucEmpresa || !groupId) return;

  const membersCurrent = unique(currentMemberDnis);
  const membersPrevious = unique(previousMemberDnis);
  const sedesCurrent = unique(currentSedeIds);
  const sedesPrevious = unique(previousSedeIds);

  const addedMembers = membersCurrent.filter((dni) => !membersPrevious.includes(dni));
  const removedMembers = membersPrevious.filter((dni) => !membersCurrent.includes(dni));
  const retainedMembers = membersCurrent.filter((dni) => membersPrevious.includes(dni));
  const removedSedes = sedesPrevious.filter((s) => !sedesCurrent.includes(s));

  const membershipBatch = writeBatch(db);
  membersCurrent.forEach((dni) => {
    const membershipRef = doc(db, `grupos/${rucEmpresa}/memberships`, String(dni));
    membershipBatch.set(
      membershipRef,
      {
        rucEmpresa,
        dni: String(dni),
        groupId: String(groupId),
        groupName: String(groupName || ""),
        sedeIds: sedesCurrent,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  });
  removedMembers.forEach((dni) => {
    const membershipRef = doc(db, `grupos/${rucEmpresa}/memberships`, String(dni));
    membershipBatch.delete(membershipRef);
  });
  await membershipBatch.commit();

  const selectedSedesData = sedesData.filter((s) => sedesCurrent.includes(String(s.id)));

  // Una lectura por miembro para saber con qué documento cuenta ya cada sede:
  // la escritura va SIEMPRE al documento existente (cualquier ID) para no crear
  // otro, y solo se usa el ID canónico `${dni}_${sedeId}` cuando la sede es
  // nueva para ese trabajador.
  const documentosPorMiembro = new Map();
  await ejecutarEnLotes(membersCurrent, async (dni) => {
    const mapa = new Map();
    const snap = await getDocs(collection(db, `asistencia/${dni}/coordenadas`));
    snap.forEach((coordDoc) => {
      const datos = coordDoc.data() || {};
      const sedeId = String(datos.id_sede_empresa || "");
      if (!sedeId) return;
      const actual = mapa.get(sedeId);
      const esCanonico = coordDoc.id === `${dni}_${sedeId}`;
      if (!actual || esCanonico) mapa.set(sedeId, { id: coordDoc.id, datos });
    });
    documentosPorMiembro.set(dni, mapa);
  });

  // Todas las asignaciones van al documento existente o, si no hay, al
  // canónico `${dni}_${sedeId}`, y la procedencia del grupo se acumula con
  // arrayUnion: varios grupos (y una asignación individual) pueden mantener el
  // mismo documento sin duplicarlo. Se indexa por ruta para no escribir dos
  // veces el mismo documento en un batch.
  const operaciones = new Map();

  const agregarAsignacion = (dni, sede, crearMarcaDeFecha) => {
    const existente = documentosPorMiembro.get(dni)?.get(String(sede.id)) || null;

    // Si el documento existente venía de una asignación manual (sin marcas de
    // grupo), se conserva esa procedencia para que quitar el grupo no borre la
    // sede que el administrador asignó a mano
    const procedenciaExistente = existente ? leerProcedencia(existente.datos) : null;

    const op = operacionAsignacionSede({
      dni,
      ruc: rucEmpresa,
      sede,
      companyName,
      grupoId: groupId,
      grupoNombre: groupName,
      crearMarcaDeFecha,
      idDocumento: existente?.id || null,
      individual: procedenciaExistente?.asignadoIndividual || false,
    });

    const previa = operaciones.get(op.ref.path);
    operaciones.set(
      op.ref.path,
      previa ? { ref: op.ref, data: { ...previa.data, ...op.data } } : op
    );
  };

  membersCurrent.forEach((dni) => {
    const esNuevo = addedMembers.includes(dni);
    selectedSedesData.forEach((sede) => agregarAsignacion(dni, sede, esNuevo));
  });

  // Bajas: se quita SOLO la procedencia de este grupo. El documento se borra
  // únicamente si ninguna otra procedencia lo mantiene.
  const bajas = [];
  removedMembers.forEach((dni) =>
    sedesPrevious.forEach((sedeId) => bajas.push({ dni, sedeId }))
  );
  retainedMembers.forEach((dni) =>
    removedSedes.forEach((sedeId) => bajas.push({ dni, sedeId }))
  );

  const setOps = [...operaciones.values()];
  const maxOps = 400;
  for (let i = 0; i < setOps.length; i += maxOps) {
    const batch = writeBatch(db);
    setOps.slice(i, i + maxOps).forEach((op) => batch.set(op.ref, op.data, { merge: true }));
    await batch.commit();
  }

  await ejecutarEnLotes(bajas, ({ dni, sedeId }) =>
    quitarSedeDeUsuario({ dni, sedeId, grupoId: groupId })
  );

  if (sedesCurrent.length > 0) {
    const batchLink = writeBatch(db);
    sedesCurrent.forEach((sedeId) => {
      const sedeRef = doc(db, `company_cordenadas/${rucEmpresa}/sedes`, sedeId);
      batchLink.update(sedeRef, {
        groupId: String(groupId),
        groupName: String(groupName || ""),
        updatedAt: serverTimestamp(),
      });
    });
    await batchLink.commit();
  }

  if (removedSedes.length > 0) {
    await limpiarVinculosGrupoEnSedes({ rucEmpresa, groupId, sedeIds: removedSedes });
  }

  await Promise.all(
    membersCurrent.map((dni) =>
      limpiarCoordenadasGrupoDuplicadasUsuario({
        dni,
        groupId,
        allowedSedeIds: sedesCurrent,
      })
    )
  );
  await Promise.all(
    removedMembers.map((dni) =>
      limpiarCoordenadasGrupoDuplicadasUsuario({
        dni,
        groupId,
        allowedSedeIds: [],
      })
    )
  );
};

/**
 * Quita la procedencia de este grupo en las sedes que ya no le corresponden al
 * trabajador. Antes esta función borraba además todo documento cuyo ID no
 * fuese `${grupoId}_${sedeId}`, lo que con el ID canónico nuevo habría borrado
 * justo el documento recién escrito; ahora la decisión se toma por procedencia
 * (`grupoId` / `grupos`) y el borrado real lo resuelve quitarSedeDeUsuario.
 */
export const limpiarCoordenadasGrupoDuplicadasUsuario = async ({
  dni,
  groupId,
  allowedSedeIds = [],
}) => {
  const dniStr = String(dni || "");
  const groupIdStr = String(groupId || "");
  if (!dniStr || !groupIdStr) return;

  const allowed = new Set(unique(allowedSedeIds));
  const snap = await getDocs(collection(db, `asistencia/${dniStr}/coordenadas`));
  const sedesARevisar = new Set();

  snap.forEach((coordDoc) => {
    const data = coordDoc.data() || {};
    const grupoIdDoc = String(data.grupoId || "");
    const gruposDoc = Array.isArray(data.grupos) ? data.grupos : [];
    const esDeEsteGrupo =
      grupoIdDoc === groupIdStr ||
      gruposDoc.some((g) => String(g?.id || g?.grupoId || "") === groupIdStr);

    if (!esDeEsteGrupo) return;

    const sedeId = String(data.id_sede_empresa || "");
    if (!sedeId) return;
    if (allowed.size === 0 || !allowed.has(sedeId)) {
      sedesARevisar.add(sedeId);
    }
  });

  await Promise.all(
    [...sedesARevisar].map((sedeId) =>
      quitarSedeDeUsuario({ dni: dniStr, sedeId, grupoId: groupIdStr })
    )
  );
};
